import { roundMoney } from './calculations';

export type RecurringPriceChangeType = 'increase_percent' | 'increase_amount';
export type RecurringGstBasis = 'inclusive' | 'exclusive';

export interface RecurringPriceChange {
  id: string;
  effectiveFrom: string;
  changeType: RecurringPriceChangeType;
  value: number;
  gstBasis: RecurringGstBasis;
  note?: string;
  targetSubtotalExTax: number;
  targetTotalInclTax: number;
}

export interface RecurringPricingSnapshot {
  subtotal: number;
  taxAmount: number;
  total: number;
}

const cleanDate = (value: unknown): string => String(value || '').slice(0, 10);

export const getRecurringScheduledPriceChanges = (template: any): RecurringPriceChange[] => {
  if (!Array.isArray(template?.scheduledPriceChanges)) return [];
  return template.scheduledPriceChanges
    .filter((change: any) => change && cleanDate(change.effectiveFrom) && Number(change.value) > 0)
    .map((change: any) => ({
      id: String(change.id || ''),
      effectiveFrom: cleanDate(change.effectiveFrom),
      changeType: change.changeType === 'increase_amount' ? 'increase_amount' : 'increase_percent',
      value: Number(change.value || 0),
      gstBasis: change.gstBasis === 'exclusive' ? 'exclusive' : 'inclusive',
      note: String(change.note || ''),
      targetSubtotalExTax: roundMoney(Number(change.targetSubtotalExTax || 0)),
      targetTotalInclTax: roundMoney(Number(change.targetTotalInclTax || 0)),
    }))
    .sort((a: RecurringPriceChange, b: RecurringPriceChange) => a.effectiveFrom.localeCompare(b.effectiveFrom));
};

export const recalculateRecurringPriceChanges = (
  basePricing: RecurringPricingSnapshot,
  changes: Array<Partial<RecurringPriceChange>>,
): RecurringPriceChange[] => {
  let subtotal = roundMoney(Number(basePricing.subtotal || 0));
  let taxAmount = roundMoney(Number(basePricing.taxAmount || 0));
  let total = roundMoney(Number(basePricing.total || 0));
  const taxRatio = subtotal > 0 ? taxAmount / subtotal : 0;

  return [...changes]
    .filter(change => cleanDate(change.effectiveFrom) && Number(change.value) > 0)
    .sort((a, b) => cleanDate(a.effectiveFrom).localeCompare(cleanDate(b.effectiveFrom)))
    .map((change, index) => {
      const value = Number(change.value || 0);
      const changeType: RecurringPriceChangeType = change.changeType === 'increase_amount' ? 'increase_amount' : 'increase_percent';
      const gstBasis: RecurringGstBasis = change.gstBasis === 'exclusive' ? 'exclusive' : 'inclusive';

      if (changeType === 'increase_percent') {
        const factor = 1 + value / 100;
        subtotal = roundMoney(subtotal * factor);
        taxAmount = roundMoney(taxAmount * factor);
        total = roundMoney(total * factor);
      } else if (gstBasis === 'exclusive') {
        const subtotalIncrease = roundMoney(value);
        const taxIncrease = roundMoney(subtotalIncrease * taxRatio);
        subtotal = roundMoney(subtotal + subtotalIncrease);
        taxAmount = roundMoney(taxAmount + taxIncrease);
        total = roundMoney(subtotal + taxAmount);
      } else {
        const totalIncrease = roundMoney(value);
        const subtotalIncrease = taxRatio > 0 ? roundMoney(totalIncrease / (1 + taxRatio)) : totalIncrease;
        subtotal = roundMoney(subtotal + subtotalIncrease);
        taxAmount = roundMoney(total + totalIncrease - subtotal);
        total = roundMoney(total + totalIncrease);
      }

      return {
        id: String(change.id || `price-change-${index + 1}`),
        effectiveFrom: cleanDate(change.effectiveFrom),
        changeType,
        value,
        gstBasis,
        note: String(change.note || ''),
        targetSubtotalExTax: subtotal,
        targetTotalInclTax: total,
      };
    });
};

export const getApplicableRecurringPriceChange = (
  template: any,
  servicePeriodStart: string,
): RecurringPriceChange | null => {
  const serviceStart = cleanDate(servicePeriodStart);
  if (!serviceStart) return null;
  const applicable = getRecurringScheduledPriceChanges(template)
    .filter(change => change.effectiveFrom <= serviceStart && change.targetTotalInclTax > 0);
  return applicable.length ? applicable[applicable.length - 1] : null;
};